<template>
	<div>
		<div class="main" v-if="!isLoading">
			<div style="min-height: 50vh">
				<div class="flex space-x-8">
					<BaseSelect class="w-full" v-model="status" :options="insuranceStatus" label="Status" />
				</div>
				<div class="flex space-x-8" v-if="isInsuranceNumbersAllowed">
					<BaseInput class="w-1/2" v-model="model.opposite_case_number" :label="$t('case_number')" />
					<BaseInput class="w-1/2" v-model="model.opposite_insurance_contract_number" :label="$t('contract_number')" />
				</div>
				<div class="opposite-insurance-modal">
					<CompanyForm
						ref="CompanyForm"
						v-if="isInsuranceStatusAllowed"
						v-model="model.oppositeInsurance"
						:options="options"
						:custom-label="$t('foreign_insurance').toString()"
						:options-label="$t('insurance').toString()"
						:highlight_suffix="$t('add_new')"
						:show-postal-options="true"
					/>
				</div>

				<div v-if="showOppositeCauser" class="text-lg mb-4 font-bold">{{ $t('opponent_data') }}</div>
				<div v-if="showOppositeCauser" class="flex flex-wrap w-full">
					<div v-if="hasPerson" class="flex flex-wrap w-full">
						<PersonNameForm
							ref="PersonNameForm"
							v-model="model.oppositePerson"
							:show-company="false"
							class="w-full"
							@input="null"
							:disabled="true"
						/>
					</div>
					<LicensePlateInput ref="LicensePlateInput" v-model="model.oppositeLicensePlate" @input="null" :disabled="true" v-else />
				</div>
			</div>
		</div>
		<div v-else>
			<LoadingSpinner />
		</div>
		<div class="flex justify-end">
			<SaveButton :disabled="isLoading" @click="save" />
		</div>
	</div>
</template>

<script>
import { DamagecasesApiFactory, InsurancesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import CompanyForm from '@/components/damagecases/forms/CompanyForm';
import stateMerge from 'vue-object-merge';
import { makeShadowCopy } from '@/plugins/Utils';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseInput from '@/components/forms/inputs/BaseInput';
import Validate from '@/mixins/Validate';
import OppositeInsuranceStatus from '@/mixins/damagecase/OppositeInsuranceStatus';
import SaveButton from '@/components/forms/buttons/SaveButton';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import PersonNameForm from '@/components/forms/PersonNameForm.vue';
import LicensePlateInput from '@/components/forms/inputs/LicensePlateInput.vue';

export default {
	name: 'OppositeInsurance',
	mixins: [Validate, OppositeInsuranceStatus],
	components: { LicensePlateInput, PersonNameForm, LoadingSpinner, SaveButton, BaseInput, BaseSelect, CompanyForm },
	data() {
		return {
			opposite_case_number: null,
			status: null,
			isLoading: true,
			model: {
				city: {
					zipCode: null,
					name: null,
				},
			},
			oppositePerson: null,
			oppositeLicensePlate: null,
			options: [],
		};
	},
	computed: {
		insuranceStatus() {
			return this.$store.getters.isProcessComprehensive ? this.comprehensiveOppositeInsuranceStatus : this.oppositeInsuranceStatus;
		},
		isInsuranceStatusAllowed() {
			return [this.insuranceStatus[0], this.insuranceStatus[1]].includes(this.status);
		},
		showOppositeCauser() {
			return [this.insuranceStatus[2]].includes(this.status) && (this.hasPerson || this.hasLicensePlate);
		},
		isInsuranceNumbersAllowed() {
			return [this.insuranceStatus[0], this.insuranceStatus[1], this.insuranceStatus[4]].includes(this.status);
		},
		hasPerson() {
			return this.model.oppositePerson !== undefined && this.model.oppositePerson !== null;
		},
		hasLicensePlate() {
			return this.model.oppositeLicensePlate !== undefined && this.model.oppositeLicensePlate !== null;
		},
	},
	methods: {
		updateDamageCaseStore() {
			this.$store.dispatch('forceUpdateDamageCase', { id: parseInt(this.$route.params.processId || this.$route.params.id) });
		},
		getDamageCase() {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdGet(parseInt(this.$route.params.processId || this.$route.params.id))
				.then(response => {
					stateMerge(this.model, response.data);
					this.model = makeShadowCopy(this.model);
					const oppositeInsuranceState = this.model.opposite_insurance_state;
					const statusAsOption = this.insuranceStatus.find(option => option.value === oppositeInsuranceState);
					this.status = statusAsOption || this.insuranceStatus[0];
					this.isLoading = false;
				})
				.catch(error => {
					this.isLoading = false;
				})
				.finally(_ => {
					this.isLoading = false;
				});
		},
		save() {
			const oppositeInsurance = this.model.oppositeInsurance;
			const isCustomCompany = oppositeInsurance?.isEditable && oppositeInsurance?.id;

			if (!this.validate()) {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				return;
			}
			this.isLoading = true;
			this.$store.commit('setConfirmLeaving', false);

			if (!this.isInsuranceStatusAllowed) {
				this.updateDamagecase(this.$t('insurance_data_updated'));
			} else if (!isCustomCompany) {
				this.updateDamagecase(this.$t('assigned_insurance_damage_case'));
			} else {
				this.updateOppositeInsurance().then(() => {
					this.updateDamagecase(this.$t('insurance_data_updated'));
				});
			}
		},
		updateOppositeInsurance() {
			return InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.insurancesIdPut(this.model.oppositeInsurance.id, this.model.oppositeInsurance)
				.catch(error => {
					this.isLoading = false;
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
		updateDamagecase(messageBoxText) {
			this.model.opposite_insurance_state = this.status.value;
			//const isInsuranceSet = this.status === (this.insuranceStatus[0] || this.insuranceStatus[1]);
			const isInsuranceSet = this.isInsuranceStatusAllowed;
			const damagecasesApiFactory = DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios);
			return damagecasesApiFactory
				.damagecasesIdOppositeInsurancePut(
					parseInt(this.$route.params.processId || this.$route.params.id),
					isInsuranceSet ? this.model.oppositeInsurance : null,
				)
				.then(response => {
					damagecasesApiFactory
						.damagecasesIdPut(this.$route.params.processId || this.$route.params.id, {
							...this.model,
							customer: undefined,
							vehicle: undefined,
							overviewImage: undefined,
							_shadow: undefined,
						})
						.then(() => {
							this.isLoading = false;
							if (messageBoxText) {
								this.$swal({
									title: this.$t('general.success').toString(),
									text: messageBoxText,
									icon: 'success',
								}).then(result => {
									this.$store.commit('setConfirmLeaving', false);
									this.updateDamageCaseStore();
									this.$router.push({
										name: 'management.process.edit',
										params: { processId: this.$store.getters.getDamageCase.id, customer: this.$store.getters.getDamageCase.customer.id },
									});
								});
							}
						});
				})
				.catch(error => {
					this.isLoading = false;
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
	},
	mounted() {
		this.getDamageCase();

		InsurancesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
			.insurancesGet()
			.then(res => {
				this.options = res.data.filter(x => x.is_system_insurance);
			})
			.catch(error => {
				this.$swal(this.$t('general.attention').toString(), this.$t('no_companies_found').toString(), 'warning');
			});
	},
};
</script>

<style scoped>
::v-deep .opposite-insurance-modal .multiselect__content-wrapper {
	max-height: 180px !important;
	overflow-y: auto;
}
</style>
